import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMegaBonus } from "@/hooks/useMegaBonus";
import { toast } from "@/hooks/use-toast";
import { useCallback, useMemo } from "react";
import {
  type PlanoCNPJ,
  type TipoLinha,
  getPlanoById,
  registrarCatalogoRuntime,
  PLANOS_MOVEL_CNPJ,
  PLANOS_RENOVACAO_CNPJ,
  PLANOS_PORTABILIDADE_CNPJ,
  PLANOS_FIBRA,
  PLANOS_TV,
  ADICIONAIS_MOVEL,
  registrarAdicionaisMovelRuntime,
  registrarAdicionaisCatalogoRuntime,
} from "@/constants/planosCNPJ";

export type PlanoCatalogoCategoria =
  | "movel_migracao"
  | "movel_migracao_pre"
  | "movel_renovacao"
  | "movel_portabilidade"
  | "movel_bloco"
  | "fibra"
  | "tv"
  | "combo"
  | "adicional_tv"
  | "adicional_fibra"
  | "adicional_movel"
  | "aparelhos";

export interface PlanoCatalogoRow {
  id: string;
  tipo_pessoa?: "PF" | "PJ";
  categoria: PlanoCatalogoCategoria;
  codigo: string;
  nome: string;
  valor: number;
  gb: number | null;
  velocidade_mbps: number | null;
  metadata: Record<string, unknown>;
  ativo: boolean;
  ordem: number;
  // Catalogo central pra IA: imagem do plano + descricao + diferencial.
  // Adicionados em 2026-05-24 pra qualquer agente IA poder mandar a
  // imagem via tool enviar_imagem_plano e usar descricao/diferencial
  // como contexto da argumentacao.
  imagem_url: string | null;
  descricao: string | null;
  diferencial: string | null;
  // Vínculo por opção: quando setado, esta opção vira ESTE produto do pedido na conversão.
  produto_pedido_id: string | null;
}

export const PLANOS_CATALOGO_QUERY_KEY = ["planos-proposta-catalogo"] as const;

// Índice estático por código (união de todos os planos do código) — usado para
// enriquecer cada linha do banco com os campos que só o código tem (benefícios,
// megabônus, destaque, passaporte, maxLinhasBan, velocidade).
const STATIC_BY_ID: Map<string, PlanoCNPJ> = new Map(
  [
    ...PLANOS_MOVEL_CNPJ,
    ...PLANOS_RENOVACAO_CNPJ,
    ...PLANOS_PORTABILIDADE_CNPJ,
    ...PLANOS_FIBRA,
    ...PLANOS_TV,
  ].map((p) => [p.id, p] as const),
);

function tipoPadraoDaCategoria(cat: string): PlanoCNPJ["tipo"] {
  if (cat === "fibra") return "FIBRA";
  if (cat === "tv") return "COMBO";
  return "MOVEL";
}

/** Rótulo da velocidade: até 999 mostra em MB; múltiplo de 1000 vira GB (1000 → "1GB"). */
function velocidadeLabel(mbps: number): string {
  return mbps >= 1000 && mbps % 1000 === 0 ? `${mbps / 1000}GB` : `${mbps}MB`;
}

/**
 * CPF (Douglas 29/09/2026): família do plano móvel e quantos dependentes ele leva.
 * Vem de planos_proposta_catalogo.metadata — a mesma fonte que trava o pedido.
 */
function familiaDoRow(r: PlanoCatalogoRow): Partial<PlanoCNPJ> {
  const m = r.metadata ?? {};
  const familia = m.familia_movel;
  if (familia !== "POS" && familia !== "CONTROLE" && familia !== "DEPENDENTE") return {};
  return {
    familiaMovel: familia,
    ...(m.plano_claro ? { planoClaro: String(m.plano_claro) } : {}),
    ...(m.dependentes_inclusos != null ? { dependentesInclusos: Number(m.dependentes_inclusos) } : {}),
    ...(m.dependentes_pagos_max != null ? { dependentesPagosMax: Number(m.dependentes_pagos_max) } : {}),
    ...(m.dependente === "incluso" || m.dependente === "pago" || m.dependente === "dados" ? { dependente: m.dependente } : {}),
    ...(m.modalidade ? { modalidade: String(m.modalidade) } : {}),
  };
}

/** Converte uma linha do catálogo do banco em PlanoCNPJ (enriquecido pelo estático por código). */
function rowToPlano(r: PlanoCatalogoRow): PlanoCNPJ {
  const base = STATIC_BY_ID.get(r.codigo);
  return {
    ...(base ?? {}),
    id: r.codigo,
    tipoPessoa: r.tipo_pessoa ?? "PJ",
    nome: r.nome,
    ...(String(r.categoria) === "servico_digital" ? {} : { gb: r.gb ?? 0 }),
    valor: r.valor,
    tipo: base?.tipo ?? tipoPadraoDaCategoria(r.categoria),
    beneficios: base?.beneficios ?? [],
    produtoPedidoId: r.produto_pedido_id ?? null,
    categoria: r.categoria,
    ...(r.velocidade_mbps != null ? { velocidade: velocidadeLabel(r.velocidade_mbps) } : {}),
    ...(Number(r.metadata?.meses_promocionais) > 0 && Number(r.metadata?.valor_apos_promocao) > 0
      ? { mesesPromocionais: Number(r.metadata.meses_promocionais), valorAposPromocao: Number(r.metadata.valor_apos_promocao) }
      : {}),
    ...(r.metadata?.praca ? { praca: String(r.metadata.praca) } : {}),
    ...familiaDoRow(r),
  } as PlanoCNPJ;
}

/**
 * Catálogo dinâmico de planos da proposta.
 * - Snapshot histórico: NÃO altera valores de pedidos já criados (itens_pedido guarda valor_unit próprio).
 * - Fallback: se a query falhar/estiver vazia, o consumidor deve cair no catálogo estático (planosCNPJ.ts).
 */
export function usePlanosPropostaCatalogo(tipoPessoa: "PF" | "PJ" | "TODOS" = "PJ") {
  const queryClient = useQueryClient();
  // Quem usa o catálogo monta proposta: carrega junto o Mega Bônus do banco.
  useMegaBonus();

  const query = useQuery({
    queryKey: PLANOS_CATALOGO_QUERY_KEY,
    queryFn: async (): Promise<PlanoCatalogoRow[]> => {
      const { data, error } = await supabase
        .from("planos_proposta_catalogo" as any)
        .select("*")
        .order("categoria")
        .order("ordem");

      if (error) throw error;
      const rows = ((data ?? []) as any[]).map((r) => ({
        ...r,
        valor: Number(r.valor) || 0,
        gb: r.gb == null ? null : Number(r.gb),
        velocidade_mbps: r.velocidade_mbps == null ? null : Number(r.velocidade_mbps),
        metadata: r.metadata ?? {},
        produto_pedido_id: r.produto_pedido_id ?? null,
      })) as PlanoCatalogoRow[];
      // Alimenta o resolvedor global getPlanoById para planos criados via banco/UI.
      registrarCatalogoRuntime(rows.filter(r => r.metadata?.escopo !== "referencia_comercial_pf").map(rowToPlano));
      registrarAdicionaisCatalogoRuntime(
        rows.filter((row) => row.categoria === "adicional_tv" && row.ativo && row.codigo.startsWith("cpf-"))
          .map((row) => ({ id: row.codigo, nome: row.nome, valor: row.valor })),
        rows.filter((row) => row.categoria === "adicional_fibra" && row.ativo && row.codigo.startsWith("cpf-"))
          .map((row) => ({ id: row.codigo, nome: row.nome, valor: row.valor })),
      );
      registrarAdicionaisMovelRuntime(
        rows
          .filter((row) => row.categoria === "adicional_movel" && row.ativo && (row.tipo_pessoa ?? "PJ") === "PJ")
          .map((row) => {
            const fallback = ADICIONAIS_MOVEL.find((adicional) => adicional.id === row.codigo);
            const metadataProdutoId = typeof row.metadata?.produtoId === "string"
              ? row.metadata.produtoId
              : null;
            return {
              id: row.codigo,
              nome: row.nome,
              valor: row.valor,
              gb: row.gb,
              produtoId: row.produto_pedido_id ?? metadataProdutoId ?? fallback?.produtoId ?? "",
              descricao: typeof row.metadata?.descricao === "string"
                ? row.metadata.descricao
                : fallback?.descricao,
            };
          })
          .filter((adicional) => adicional.produtoId),
      );
      return rows;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const rowsSegmento = useMemo(() => (query.data ?? []).filter((row) =>
    tipoPessoa === "TODOS" || (row.tipo_pessoa ?? "PJ") === tipoPessoa,
  ), [query.data, tipoPessoa]);

  const updatePlano = useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<Pick<
        PlanoCatalogoRow,
        | "nome" | "valor" | "gb" | "velocidade_mbps" | "ativo" | "ordem"
        | "imagem_url" | "descricao" | "diferencial" | "produto_pedido_id" | "metadata"
      >>;
    }) => {
      const { error } = await supabase
        .from("planos_proposta_catalogo" as any)
        .update(input.patch as any)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLANOS_CATALOGO_QUERY_KEY });
      toast({ title: "Plano atualizado", description: "Os novos valores valem para novas propostas. Pedidos existentes não são afetados." });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao atualizar", description: err?.message ?? String(err), variant: "destructive" });
    },
  });

  const createPlano = useMutation({
    mutationFn: async (input: {
      tipo_pessoa?: "PF" | "PJ";
      categoria: PlanoCatalogoCategoria | string;
      codigo: string;
      nome: string;
      valor: number;
      gb?: number | null;
      velocidade_mbps?: number | null;
      ativo?: boolean;
      ordem?: number;
      produto_pedido_id?: string | null;
      metadata?: Record<string, unknown>;
    }) => {
      const payload = {
        tipo_pessoa: input.tipo_pessoa ?? (tipoPessoa === "PF" ? "PF" : "PJ"),
        categoria: input.categoria,
        codigo: input.codigo.trim(),
        nome: input.nome.trim(),
        valor: Number(input.valor) || 0,
        gb: input.gb ?? null,
        velocidade_mbps: input.velocidade_mbps ?? null,
        ativo: input.ativo ?? true,
        ordem: input.ordem ?? 0,
        produto_pedido_id: input.produto_pedido_id ?? null,
        ...(input.metadata ? { metadata: input.metadata } : {}),
      };
      const { data, error } = await supabase
        .from("planos_proposta_catalogo" as any)
        .insert([payload as any])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLANOS_CATALOGO_QUERY_KEY });
      toast({ title: "Variação criada", description: "Já está disponível para novas propostas." });
    },
    onError: (err: any) => {
      const isDuplicate = err?.code === "23505" || /duplicate/i.test(err?.message ?? "");
      toast({
        title: isDuplicate ? "Código já existe" : "Erro ao criar variação",
        description: isDuplicate
          ? "Já existe uma variação com esse código. Escolha outro."
          : err?.message ?? String(err),
        variant: "destructive",
      });
    },
  });

  const deletePlano = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("planos_proposta_catalogo" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLANOS_CATALOGO_QUERY_KEY });
      toast({ title: "Variação removida" });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao remover", description: err?.message ?? String(err), variant: "destructive" });
    },
  });

  /**
   * Mapa para lookup rápido por categoria + codigo.
   */
  const byCodigo = useMemo(() => {
    const m = new Map<string, PlanoCatalogoRow>();
    for (const row of rowsSegmento) {
      m.set(`${row.categoria}::${row.codigo}`, row);
    }
    return m;
  }, [rowsSegmento]);

  /**
   * Aplica overrides do banco (valor/nome) sobre um plano estático.
   * Se o plano não existir no banco, devolve o estático intacto (fallback).
   */
  const withOverride = useCallback(<T extends { id: string; nome: string; valor: number; gb?: number }>(
    categoria: PlanoCatalogoCategoria,
    plano: T,
  ): T => {
    const row = byCodigo.get(`${categoria}::${plano.id}`);
    if (!row) return plano;
    if (row.ativo === false) return { ...plano, valor: row.valor, nome: row.nome };
    return {
      ...plano,
      nome: row.nome,
      valor: row.valor,
      ...(row.gb != null ? { gb: row.gb } : {}),
    };
  }, [byCodigo]);

  /** Aplica withOverride em um array inteiro de planos. */
  const withOverrides = useCallback(<T extends { id: string; nome: string; valor: number; gb?: number }>(
    categoria: PlanoCatalogoCategoria,
    planos: T[],
  ): T[] => {
    return planos.filter((p) => tipoPessoa !== "PF" || byCodigo.has(`${categoria}::${p.id}`)).map((p) => withOverride(categoria, p));
  }, [withOverride, tipoPessoa, byCodigo]);

  /**
   * DB-driven: monta a lista de uma categoria A PARTIR do catálogo do banco
   * (a "membership" passa a ser o banco), enriquecendo cada linha com os campos
   * extras do estático — por código, ou por GB como reserva (útil p/ o Pré, que
   * espelha o Pós). Filtra só ativos e ordena por `ordem`. Se o catálogo estiver
   * vazio/indisponível, devolve o estático (fallback seguro: nunca quebra a proposta).
   */
  const withCatalogo = useCallback(<T extends { id: string; nome: string; valor: number; gb?: number; velocidade?: string }>(
    categoria: PlanoCatalogoCategoria,
    estaticos: T[],
    defaults?: Partial<T>,
  ): T[] => {
    const porId = new Map(estaticos.map((p) => [p.id, p] as const));
    const porGb = new Map<number, T>();
    for (const p of estaticos) {
      const g = (p as { gb?: number }).gb;
      if (typeof g === "number" && !porGb.has(g)) porGb.set(g, p);
    }
    const rowsCat = rowsSegmento
      .filter((r) => r.categoria === categoria && r.ativo && r.metadata?.escopo !== "referencia_comercial_pf"
        && r.metadata?.familia_movel !== "DEPENDENTE")
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    if (rowsCat.length === 0) return tipoPessoa === "PF" ? [] : estaticos; // fallback seguro
    return rowsCat.map((r) => {
      const base = porId.get(r.codigo) ?? (tipoPessoa !== "PF" && r.gb != null ? porGb.get(r.gb) : undefined);
      return {
        ...(defaults ?? {}),
        ...(base ?? {}),
        id: r.codigo,
        tipoPessoa: r.tipo_pessoa ?? "PJ",
        nome: r.nome,
        valor: r.valor,
        ...(r.gb != null ? { gb: r.gb } : {}),
        ...(r.velocidade_mbps != null ? { velocidade: velocidadeLabel(r.velocidade_mbps) } : {}),
        ...(r.produto_pedido_id ? { produtoPedidoId: r.produto_pedido_id } : {}),
        ...familiaDoRow(r),
        categoria: r.categoria,
      } as unknown as T;
    });
  }, [rowsSegmento, tipoPessoa]);

  /** O plano de dependente CPF da categoria (incluído R$ 0, pago R$ 55, só dados R$ 29,99). */
  const planoDependenteCpf = useCallback((categoria: string, tipo: "incluso" | "pago" | "dados"): PlanoCNPJ | undefined => {
    const row = rowsSegmento.find((r) => r.ativo && r.tipo_pessoa === "PF" && r.metadata?.familia_movel === "DEPENDENTE"
      && r.metadata?.dependente === tipo && (tipo === "dados" || r.categoria === categoria));
    return row ? rowToPlano(row) : undefined;
  }, [rowsSegmento]);

  return {
    ...query,
    rows: rowsSegmento,
    planoDependenteCpf,
    byCodigo,
    withOverride,
    withOverrides,
    withCatalogo,
    updatePlano,
    createPlano,
    deletePlano,
  };
}

/**
 * Mapeia (tipo da linha + id do plano) para a categoria do catalogo,
 * espelhando o `getPlanosPorTipo` do MontadorComposicaoPlano.
 * Necessario porque os `codigo` se repetem entre categorias
 * (ex.: `ren-11` existe em `movel_renovacao` E `renov`), entao a
 * resolucao precisa saber a categoria certa.
 */
function categoriaDoPlano(tipo: TipoLinha | undefined, planoId: string): PlanoCatalogoCategoria | null {
  switch (tipo) {
    case "FIBRA": return "fibra";
    case "TV": return "tv";
    case "PORTABILIDADE": return "movel_portabilidade";
    case "TT_PRE": return "movel_migracao_pre";
    default: break;
  }
  // Tipos que misturam arrays no seletor (RENOVACAO/NOVA/TT/LINHA_NOVA): decidir pelo prefixo do id.
  if (planoId.startsWith("ren-")) return "movel_renovacao";
  if (planoId.startsWith("bloco-")) return "movel_bloco";
  if (planoId.startsWith("pos-")) return "movel_migracao";
  if (planoId.startsWith("port-")) return "movel_portabilidade";
  if (planoId.startsWith("fibra-")) return "fibra";
  if (planoId.startsWith("tv-")) return "tv";
  return null; // combo e desconhecidos -> mantem o estatico
}

/**
 * Resolve um plano por id aplicando os overrides do catalogo do banco,
 * usando a MESMA categoria que o seletor (`getPlanosPorTipo`/`withCatalogo`) usa.
 * `getPlanoById` consulta o estatico primeiro, entao um plano que existe no
 * estatico (ex.: ren-11 = 11GB) nunca pegava o valor do catalogo (12GB) — fazendo
 * o resumo/encaminhamento de propostas hidratadas mostrar a franquia antiga.
 */
export function useResolverPlanoCatalogo(tipoPessoa: "PF" | "PJ" = "PJ") {
  const { rows, withOverride } = usePlanosPropostaCatalogo(tipoPessoa);
  return useCallback(
    (planoId?: string | null, tipo?: TipoLinha): PlanoCNPJ | undefined => {
      if (!planoId) return undefined;
      // O cache da query pode ser restaurado sem executar queryFn (e sem
      // popular o Map global). CPF resolve na própria fonte do seletor.
      if (tipoPessoa === "PF") {
        const row = rows.find(r => r.codigo === planoId);
        return row ? rowToPlano(row) : undefined;
      }
      const base = getPlanoById(planoId);
      if (!base) return undefined;
      const categoria = categoriaDoPlano(tipo, planoId);
      return categoria ? withOverride(categoria, base) : base;
    },
    [withOverride, rows, tipoPessoa],
  );
}
